import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { AlertTriangle } from 'lucide-react';
import CategoryGroup from './CategoryGroup.jsx';
import AlreadyIntegratedEmptyState from './AlreadyIntegratedEmptyState.jsx';
import { useIntegrations } from '../context';
import { useAlreadyIntegrated } from '../hooks/alreadyIntegrated';
import { INTEGRATIONS_TABS } from '../constants';

/**
 * Already Integrated tab content (CT-4160): connected integrations grouped by
 * category. Reads the hero `searchQuery` (CT-4157) to filter live; zero-match
 * categories leave the DOM. The no-results empty state is CT-4162.
 *
 * UX enhancement (beyond the prototype/ticket — pending PO sign-off): the search
 * box lives up in the hero, so filtered results can land below the fold. When a
 * search settles with matches, scroll the results into view once (just under the
 * sticky tab strip), honoring `prefers-reduced-motion`. Clearing the search or a
 * no-match query never scrolls.
 */
function AlreadyIntegratedTab() {
  const { t } = useTranslation('admin-studio');
  const navigate = useNavigate();
  const { searchQuery, setActiveTab, openRequestIntegration } = useIntegrations();
  const { groups, totalMatches, isLoading, error } = useAlreadyIntegrated(searchQuery);
  const isSearching = searchQuery.trim().length > 0;

  // Stable handler so the memoized CategoryGroup / ConnectedIntegrationCard tree
  // is not re-rendered on every parent state change (e.g. each search keystroke).
  const openProvider = useCallback((slug) => navigate(slug), [navigate]);

  const resultsRef = useRef(null);
  const didAutoScrollRef = useRef(false);
  useEffect(() => {
    // Re-arm when the search is cleared, so the next fresh search scrolls again.
    if (!isSearching) {
      didAutoScrollRef.current = false;
      return;
    }
    // Scroll once when a search first yields results — not again while refining it.
    if (isLoading || totalMatches === 0 || didAutoScrollRef.current) return;
    const node = resultsRef.current;
    if (!node || typeof node.scrollIntoView !== 'function') return;
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    node.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    didAutoScrollRef.current = true;
  }, [isSearching, isLoading, totalMatches]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (error) {
    return <StatusMessage icon={AlertTriangle} title={t('common.requestFailed')} subtitle={error} />;
  }

  if (isSearching && totalMatches === 0) {
    return (
      <AlreadyIntegratedEmptyState
        query={searchQuery.trim()}
        onSearchMarketplace={() => setActiveTab(INTEGRATIONS_TABS.MARKETPLACE)}
        onRequest={() => openRequestIntegration({ prefillName: searchQuery.trim() })}
      />
    );
  }

  return (
    // gap-8 = 32px between categories (CT-4160 spec); scroll-mt clears the sticky
    // tab strip so auto-scrolled results land below it (CT-4162 enhancement).
    <div ref={resultsRef} className="flex flex-col gap-8 scroll-mt-[80px]">
      {groups.map((group) => (
        <CategoryGroup
          key={group.categoryId}
          label={group.label}
          colorClass={group.colorClass}
          count={group.count}
          items={group.items}
          onOpenProvider={openProvider}
        />
      ))}
    </div>
  );
}

export default AlreadyIntegratedTab;
